import type { OversightRegisterResponse } from '@risksmart-app/trpc/src/types';
import type { GetOversightAssessmentsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetOversightAssessmentsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

/**
 * Reshapes the register response into the GraphQL query shape so both branches
 * feed consumers identical data.
 *
 * The GraphQL selects `oversightResults` as `assessedItems`, with each leg
 * reached through a `parents` array filtered by ParentType. Drizzle cannot
 * alias one relation twice, so the register query config selects `risk` /
 * `obligation` / `document` directly alongside the result rows, and the
 * one-element `parents` arrays are synthesised here.
 *
 * All six assessed-item legs are reconstructed — controlled + uncontrolled
 * risk, obligation, document, control test and impact — to match what the
 * GraphQL leg returns; omitting any of them blanked those items in the
 * "Assessed items" column for orgs on the tRPC path.
 *
 * `assessmentResults` is destructured out rather than spread through, so the
 * mapped row carries only `assessedItems` — the field the GraphQL query
 * actually returns. Leaving both on the object would make the two branches
 * observably different, which defeats the point of the bridge.
 */
export const mapOversightRegisterToGraphQL = (
  data: OversightRegisterResponse
): GetOversightAssessmentsQuery => ({
  oversight_assessment: data.oversight.map(
    ({ assessmentResults, ...item }) => ({
      ...item,
      assessedItems: assessmentResults.map((ar) => ({
        controlledRiskAssessmentResult: ar.controlledRiskAssessmentResult
          ? {
              Id: ar.controlledRiskAssessmentResult.Id,
              parents: [{ risk: ar.risk }],
            }
          : null,
        uncontrolledRiskAssessmentResult: ar.uncontrolledRiskAssessmentResult
          ? {
              Id: ar.uncontrolledRiskAssessmentResult.Id,
              parents: [{ risk: ar.risk }],
            }
          : null,
        obligationAssessmentResult: ar.obligationAssessmentResult
          ? {
              Id: ar.obligationAssessmentResult.Id,
              parents: [{ obligation: ar.obligation }],
            }
          : null,
        documentAssessmentResult: ar.documentAssessmentResult
          ? {
              Id: ar.documentAssessmentResult.Id,
              parents: [{ document: ar.document }],
            }
          : null,
        testResult: ar.testResult
          ? {
              Id: ar.testResult.Id,
              parent: ar.testResult.parent,
            }
          : null,
        impactRating: ar.impactRating
          ? {
              Id: ar.impactRating.Id,
              impact: ar.impactRating.impact,
            }
          : null,
      })),
    })
  ),
});

/**
 * The oversight assessment register read.
 *
 * Named `...Register` after `useGetInternalAuditReportsRegister`, and
 * deliberately NOT `useGetOversightAssessments` — `useGetAllOversightAssessments`
 * already exists and wraps `frontend.oversight.getAll`, a much thinner flat
 * column subset with no relations.
 */
export const useGetOversightAssessmentsRegister = createQueryHook<
  Record<string, never>,
  OversightRegisterResponse,
  GetOversightAssessmentsQuery
>({
  trpcQueryOptions: (trpc) => trpc.frontend.oversight.register.queryOptions(),
  mapTrpcDataToGraphQL: mapOversightRegisterToGraphQL,
  graphqlDocument: GetOversightAssessmentsDocument,
});
